import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import { ArrowBack as BackIcon, Edit as EditIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

/**
 * @param {object} props
 * @param {string} props.roleId
 * @param {'page'|'dialog'} props.variant
 * @param {() => void} [props.onRequestClose] — when variant is dialog, optional close from toolbar (unused; dialog uses parent)
 */
const RoleDetailContent = ({ roleId, variant = 'page', onRequestClose }) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!roleId) {
      setLoading(false);
      setRole(null);
      return undefined;
    }
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/roles/${roleId}`);
        if (!cancelled) setRole(r.data.data);
      } catch {
        if (!cancelled) toast.error('Failed to load role');
        if (!cancelled && variant === 'page') navigate('/admin/roles');
        if (!cancelled && variant === 'dialog' && onRequestClose) onRequestClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [roleId, variant, navigate, onRequestClose]);

  if (loading || !role) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: variant === 'dialog' ? 2 : 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const rolePerms = role.permissions || role.Permissions || [];
  const grouped = rolePerms.reduce((acc, p) => {
    const m = p.module || 'other';
    if (!acc[m]) acc[m] = [];
    acc[m].push(p);
    return acc;
  }, {});

  const detailCards = (
    <>
      <Card sx={{ mb: variant === 'dialog' ? 2 : 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Role details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Reference
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {role.reference_code || '—'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Typography variant="body1">{role.is_active !== false ? 'Active' : 'Inactive'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {role.name}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Description
              </Typography>
              <Typography variant="body1">{role.description || '—'}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Permissions ({rolePerms.length})
          </Typography>
          {Object.entries(grouped)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([module, perms]) => (
              <Box key={module} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  {module.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {perms.map((p) => (
                    <Chip key={p.id} size="small" label={p.name || p.code} variant="outlined" />
                  ))}
                </Box>
              </Box>
            ))}
          {rolePerms.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No permissions assigned.
            </Typography>
          )}
        </CardContent>
      </Card>
    </>
  );

  if (variant === 'dialog') {
    return detailCards;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/roles')}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, flex: 1 }}>
          View role
        </Typography>
        {hasPermission('roles_modify') && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/admin/roles/${roleId}/edit`)}
            sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
          >
            Edit
          </Button>
        )}
      </Box>
      {detailCards}
    </Box>
  );
};

export default RoleDetailContent;
